import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthService, LoginResult} from '../../core/auth/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Language} from '@app/core/constants/language';
import {TranslateService} from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import {NGXLogger} from 'ngx-logger';
import {CookieService} from '@app/core/services/cookie.service';
import {query} from '@angular/animations';

const IOS_HINT_KEY = 'iosInstallHintDismissed:v1';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  public LoginError = LoginError;
  public Language = Language;

  /** Emits true if user is logged out, so we are ready for login */
  private readonly logoutFinishedSubject = new BehaviorSubject(false);

  public isLoadingLogin = false;
  public showCreateNotice = false;

  public isLoginV2 = false;
  public savePassword = false;

  public isIos = false;
  public isIPad = false;
  public isIosAlone = false;

  public isParentsLogin = false;

  credentials: Credentials = {
    username: '',
    password: ''
  };

  loginError = LoginError.None;

  public get language(): Language {
    return this.auth.getCurrentLanguage();
  }
  public set language(value: Language) {
    this.auth.switchLanguage(value);
    if (value === Language.English) {
      this.router.navigate(['/login/en']);
    } else if (value === Language.French) {
      this.router.navigate(['/login/fr']);
    } else {
      this.router.navigate(['/login']);
    }
  }

  constructor(private auth: AuthService,
              private log: NGXLogger,
              private translate: TranslateService,
              private route: ActivatedRoute,
              private cookie: CookieService,
              private router: Router) { }

  onLogin() {

    // First of all delete our report "markers"
    this.auth.deleteLocalStorageReports();
    this.isLoadingLogin = true;


    // Wait until we get the "go" from logout (if no logout was required, we instantly call login)
    this.logoutFinishedSubject.pipe(
      filter(finished => finished === true),
      switchMap(() => this.auth.login(this.credentials.username, this.credentials.password))
    ).subscribe({
      next: (result) => {
        if (result === LoginResult.Success || result === LoginResult.MfaRequired) {
          this.isLoadingLogin = false;
          if (this.isLoginV2 || this.isParentsLogin) this.handleLoginV2();
          this.loginError = LoginError.None;

          this.auth.isParentsApp = this.isParentsLogin;
          const target = this.isParentsLogin ? '/parents' : this.auth.loginRedirectUrl;

          if (result === LoginResult.MfaRequired) {
            // Second factor still outstanding: defer to the OTP screen, continue to target afterwards.
            this.auth.beginMfaChallenge(target);
          } else {
            this.router.navigateByUrl(target);
          }
        } else {
          this.isLoadingLogin = false;
          if (result === LoginResult.Http401) {
            this.loginError = LoginError.IncorrectCredentials;
          } else if (result === LoginResult.HttpForbidden) {
            this.loginError = LoginError.Forbidden;
          } else if (result === LoginResult.ServiceUnavailable) {
            this.loginError = LoginError.ServiceUnavailable;
          } else
          {
            this.loginError = LoginError.OtherError;
          }
        }
      }
    });
  }

  ngOnInit(): void {
    /*
     * Call logout if user is already logged in.
     * Don't keep user logged in because we don't want to prevent the user from logging in with default role
     * if user was logged in with report/parents role before.
     * This especially holds e.g. if a user is using the app for parents view (parents role), the user closes the app
     * (i.e. the app could not call logout) and now the user opens the app again and wants to login with default role.
     */

    this.checkforIos();

    this.route.params.subscribe(params => {
      // A language chosen explicitly by the user (language cookie) wins over the language of the route
      if (params['lang'] != null) { this.auth.switchLanguageFromUrl(params['lang']); }
    });

    if (this.auth.isLoggedIn) {
      this.auth.logoutObserved(false).subscribe(() => {
        this.logoutFinishedSubject.next(true);
      });
    } else {
      this.logoutFinishedSubject.next(true);
    }

    this.route.queryParams.subscribe(queryParams => {
      if (queryParams.v === '2') {
        this.initV2Login();
      }
      if (queryParams.redirect === 'parents') {
        this.auth.isParentsApp = true;
        this.isParentsLogin = true;
      }
    });

    // We are coming from a logout screen but loginV2 is still true in localStorage
    if (this.isLoginV2 === false && localStorage?.getItem('loginv2') === 'true') {
      this.initV2Login();
    }

    // Maybe we have isParentsApp in localstorage?
    this.isParentsLogin = this.auth.isParentsApp;

    if (this.isParentsLogin) {
      this.getSavedCredentials();
    }

  }

  public ngOnDestroy() {
    this.logoutFinishedSubject.complete();
  }

  private initV2Login() {
    localStorage?.setItem('loginv2', 'true');
    this.isLoginV2 = true;
    this.getSavedCredentials();
  }

  private getSavedCredentials() {
    this.credentials.username = localStorage?.getItem('username') ?? '';
    this.credentials.password = localStorage?.getItem('password') ?? '';
    this.savePassword = (localStorage?.getItem('savepassword') === 'true');
  }

  onCreateAccountClick() {
    this.showCreateNotice = !this.showCreateNotice;
  }

  checkforIos() {
    // iOS-Erkennung (inkl. iPadOS, das sich teils als Mac ausgibt)
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera || '';
    const isIPhoneIPodIPad = /iPhone|iPad|iPod/i.test(ua)
      || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);

    // PWA/Standalone erkannt?
    const isStandalone =
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      (navigator as any).standalone === true;

    // Bereits weggeklickt?
    const dismissed = this.safeGet(IOS_HINT_KEY) === '1';

    this.isIos = isIPhoneIPodIPad;
    this.isIPad = /iPad/i.test(ua) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
    this.isIosAlone = isStandalone;

    // Hinweis nur zeigen, wenn iOS, nicht Standalone, nicht bereits dismissed
    if (this.isIos && !this.isIosAlone && !dismissed) {
      this.isIos = true;   // Banner anzeigen
    } else {
      this.isIos = false;  // Banner aus
    }

    this.log.info(`PWA check: isIOS=${this.isIos}|isStandalone=${this.isIosAlone}|dismissed=${dismissed}`);
  }


  closeIosPopup() {
    this.isIos = false;
  }

  closeIosPopupForever() {
    this.safeSet(IOS_HINT_KEY, '1'); // nie wieder zeigen
    this.isIos = false;
  }



  handleLoginV2() {
    if (this.savePassword === true) {
      this.savePasswordLS();
    } else {
      this.deleteLS();
    }
  }

  savePasswordLS() {
    localStorage?.setItem('username', this.credentials.username);
    localStorage?.setItem('password', this.credentials.password);
    localStorage?.setItem('savepassword', 'true');
  }

  deleteLS() {
    localStorage?.removeItem('username');
    localStorage?.removeItem('password');
    localStorage?.removeItem('savepassword');
  }

  toggleParentsApp() {
    this.auth.isParentsApp = !this.auth.isParentsApp;
    this.isParentsLogin = this.auth.isParentsApp;
  }

  isParentsApp() {
    return this.auth.isParentsApp;
  }


  private safeGet(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
  }
  private safeSet(key: string, val: string) {
    try { localStorage.setItem(key, val); }
    catch {
      // Fallback Cookie (5 Jahre)
      const d = new Date(); d.setFullYear(d.getFullYear() + 5);
      document.cookie = `${key}=${val};expires=${d.toUTCString()};path=/;SameSite=Lax`;
    }
  }

}

interface Credentials {
  username: string;
  password: string;
}

export enum LoginError {
  None,
  IncorrectCredentials,
  OtherError,
  Forbidden,
  ServiceUnavailable
}
